/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { ItemInt } from '../config/api';

interface BrandSelectorProps {
  brands: ItemInt[];
  selectedBrands: number[];
  onBrandToggle: (brandId: number) => void;
  multiSelect?: boolean;
  maxSelection?: number;
  className?: string;
}

/**
 * Consistent brand selector component used across all dashboards
 */
const BrandSelector: React.FC<BrandSelectorProps> = ({
  brands,
  selectedBrands,
  onBrandToggle,
  multiSelect = false,
  maxSelection = 5,
  className = ''
}) => {
  const handleBrandClick = (brandId: number) => {
    if (!multiSelect) {
      // Single select mode - replace selection
      onBrandToggle(brandId);
    } else {
      // Multi-select mode
      if (selectedBrands.includes(brandId)) {
        onBrandToggle(brandId);
      } else if (selectedBrands.length < maxSelection) {
        onBrandToggle(brandId);
      }
    }
  };

  return (
    <div className={`${className}`}>
      <div className="flex flex-wrap gap-2">
        {brands.map(brand => {
          const isSelected = selectedBrands.includes(brand.id);
          return (
            <button
              key={brand.id}
              onClick={() => handleBrandClick(brand.id)}
              className={`btn ${isSelected ? 'btn-primary' : 'btn-secondary'} ${
                !isSelected && multiSelect && selectedBrands.length >= maxSelection
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
              disabled={!isSelected && multiSelect && selectedBrands.length >= maxSelection}
            >
              <span className="flex items-center">
                {isSelected && <i className="fas fa-check-circle mr-2 text-xs"></i>}
                {brand.name}
              </span>
            </button>
          );
        })}
      </div>
      {multiSelect && (
        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
          Select up to {maxSelection} brands • {selectedBrands.length} selected
        </p>
      )}
    </div>
  );
};

export default BrandSelector;
